import { useMemo } from "react";
import { useFormikContext } from "formik";
import { boolean, object, string } from "yup";
import CheckboxField from "@/components/form/CheckboxField";
import SearchableSelectField from "@/components/form/SearchableSelectField";
import { useGroups } from "@/hooks/useCommonQueries";

export const rlsUserGroupSchemaFields = {
  auto_set_rls_user_groups: boolean(),
  rls_user_group_mode: string(),
  rls_user_group_id: string(),
  rls_user_groups_readonly: boolean(),
  rls_user_group_grant: boolean(),
  rls_user_group_field: string(),
};

interface RlsUserGroupFormValues {
  parameters?: {
    auto_set_rls_user_groups?: boolean;
    rls_user_group_mode?: string;
    rls_user_group_grant?: boolean;
  };
}

export default function RlsUserGroupFields() {
  const { values } = useFormikContext<RlsUserGroupFormValues>();
  const { data: groups } = useGroups();

  const enabled = values?.parameters?.auto_set_rls_user_groups === true;
  const isSelectedMode = values?.parameters?.rls_user_group_mode === "selected";
  const grantsAccess = values?.parameters?.rls_user_group_grant === true;

  const groupOptions = useMemo(
    () => groups?.map((g) => ({ value: String(g.id), label: g.name })) || [],
    [groups],
  );

  return (
    <>
      <CheckboxField
        name="parameters.auto_set_rls_user_groups"
        label="Resolve the User's Groups"
        description="Look up the groups the user in the RLS User Field actually belongs to. Membership is resolved server-side, so a payload can never name a group the user isn't in."
      />

      {enabled && (
        <>
          <SearchableSelectField
            name="parameters.rls_user_group_mode"
            label="Which Groups"
            options={[
              { value: "all", label: "All groups the user belongs to" },
              { value: "selected", label: "One specific group" },
            ]}
            placeholder="All groups the user belongs to"
            description="Choose every group the user is a member of, or narrow it to one"
          />

          {isSelectedMode && (
            <SearchableSelectField
              name="parameters.rls_user_group_id"
              label="Group"
              options={groupOptions}
              variableToggle
              placeholder="Select a group"
              description="Only used if the user is a member of this group. Accepts a variable to resolve per run."
            />
          )}

          <CheckboxField
            name="parameters.rls_user_group_grant"
            label="Also Grant the Record to the Group"
            description="Off by default. A group grant is readable by every member, so on a team of peers each would see the others' records. Only turn this on when the group really is a shared-visibility unit."
          />

          {grantsAccess && (
            <CheckboxField
              name="parameters.rls_user_groups_readonly"
              label="Group Access is Read-only"
              description="On by default — group members can read the record but not change it"
            />
          )}

          <SearchableSelectField
            name="parameters.rls_user_group_field"
            label="Stamp Group Into Field"
            options={[
              { value: "group_id", label: "group_id" },
              { value: "team_id", label: "team_id" },
            ]}
            variableToggle
            placeholder="Leave blank to skip"
            description="Optional: write the resolved group id into this field for filtering. Only written when exactly one group resolves."
          />
        </>
      )}
    </>
  );
}

export const rlsUserGroupSchema = object(rlsUserGroupSchemaFields);
